/**
 * O cabeçalho do site público do Logigate: o logótipo, as ligações (âncoras da página, ou outras
 * páginas do site) e «Entrar». Fica transparente sobre o hero e ganha fundo ao descer.
 *
 * No telemóvel as ligações vão para um painel que abre pela direita (o `Drawer` do Mantine de
 * antes): fecha com o X, com Esc, ao tocar fora ou ao escolher uma ligação, e anima a saída.
 */
import { useEffect, useState } from "react";
import { Menu as IconeMenu, X } from "lucide-react";
import { useSaida } from "@/Logigate/ui";

const NAV_LINKS = [
    { label: "Preços", href: "#precos" },
    { label: "Prestadores", href: "#prestadores" },
    { label: "Módulos", href: "#modulos" },
    { label: "Funcionalidades", href: "#funcionalidades" },
    { label: "FAQ", href: "#faq" },
    { label: "Contactos", href: "#contacto" },
];

export interface LigacaoDoSite {
    label: string;
    /** Uma âncora (`#precos`) desliza na página; outra coisa (`/prestadores`, `/#precos`) navega. */
    href: string;
}

function scrollToSection(href: string) {
    if (!href.startsWith("#")) {
        window.location.href = href;
        return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

interface Props {
    /** Só há link "Prestadores" quando a secção dos prestadores está na página. */
    mostrarPrestadores?: boolean;
    /** As ligações de outra página do site (a dos prestadores), em vez das da página inicial. */
    links?: LigacaoDoSite[];
}

export default function WebsiteHeader({ mostrarPrestadores = false, links: ligacoes }: Props) {
    const [aberto, setAberto] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { montada, aSair } = useSaida(aberto);
    const links = ligacoes ?? NAV_LINKS.filter((link) => link.href !== "#prestadores" || mostrarPrestadores);

    useEffect(() => {
        const mudar = () => setScrolled(window.scrollY > 20);
        mudar();
        window.addEventListener("scroll", mudar, { passive: true });
        return () => window.removeEventListener("scroll", mudar);
    }, []);

    // Com o painel aberto: Esc fecha e a página por trás não desliza.
    useEffect(() => {
        if (!aberto) {
            return;
        }
        const tecla = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
        const antes = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", tecla);
        return () => {
            document.body.style.overflow = antes;
            window.removeEventListener("keydown", tecla);
        };
    }, [aberto]);

    const ir = (href: string) => {
        setAberto(false);
        // Espera o painel começar a fechar (e a página voltar a deslizar) antes de ir para a âncora.
        window.setTimeout(() => scrollToSection(href), 50);
    };

    return (
        <>
            <header className={`fixed left-0 right-0 top-10 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 shadow-sm backdrop-blur-md" : "bg-transparent"}`}>
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        <a href="/" className="flex items-center gap-2.5">
                            <img src="/logi/imagens/logo.png" alt="Logigate" className="h-9 w-9" />
                            <span className={`text-xl font-bold transition-colors ${scrolled ? "text-gray-900" : "text-white"}`}>Logigate</span>
                        </a>

                        <nav className="hidden items-center gap-1 md:flex" aria-label="Secções do site">
                            {links.map((link) => (
                                <button
                                    key={link.href}
                                    type="button"
                                    onClick={() => scrollToSection(link.href)}
                                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${scrolled ? "text-gray-600 hover:bg-gray-100 hover:text-gray-900" : "text-white/80 hover:bg-white/10 hover:text-white"}`}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </nav>

                        <div className="hidden items-center md:flex">
                            <a href="/empresas" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
                                Entrar
                            </a>
                        </div>

                        <button
                            type="button"
                            onClick={() => setAberto(true)}
                            aria-label="Abrir o menu"
                            aria-expanded={aberto}
                            aria-controls="menu-do-site"
                            className={`-mr-2 inline-flex size-11 items-center justify-center rounded-lg transition-colors md:hidden ${scrolled ? "text-gray-800 hover:bg-gray-100" : "text-white hover:bg-white/10"}`}
                        >
                            <IconeMenu className="size-6" />
                        </button>
                    </div>
                </div>
            </header>

            {montada && (
                <div className="fixed inset-0 z-[200] md:hidden">
                    <div
                        aria-hidden
                        onClick={() => setAberto(false)}
                        className={`absolute inset-0 bg-slate-950/50 backdrop-blur-[2px] ${aSair ? "animate-out fade-out-0 fill-mode-forwards duration-150" : "animate-in fade-in-0"}`}
                    />
                    <div
                        id="menu-do-site"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Menu"
                        className={`absolute inset-y-0 right-0 flex w-[min(20rem,85vw)] flex-col bg-white shadow-2xl ${aSair ? "animate-out slide-out-to-right fill-mode-forwards duration-150" : "animate-in slide-in-from-right duration-200"}`}
                    >
                        <div className="flex h-16 items-center justify-between border-b border-gray-100 px-4">
                            <span className="flex items-center gap-2">
                                <img src="/logi/imagens/logo.png" alt="" className="h-8 w-8" />
                                <span className="text-lg font-bold text-gray-900">Logigate</span>
                            </span>
                            <button type="button" onClick={() => setAberto(false)} aria-label="Fechar o menu" className="inline-flex size-11 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900">
                                <X className="size-5" />
                            </button>
                        </div>

                        <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Secções do site">
                            <ul className="space-y-1">
                                {links.map((link) => (
                                    <li key={link.href}>
                                        <button
                                            type="button"
                                            onClick={() => ir(link.href)}
                                            className="flex min-h-12 w-full items-center rounded-lg px-3 text-left text-[15px] font-medium text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700"
                                        >
                                            {link.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className="border-t border-gray-100 p-4">
                            <a href="/empresas" className="flex h-12 w-full items-center justify-center rounded-xl bg-blue-600 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
                                Entrar
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
